import { useEffect, useState } from "react";
import { inAndroidApp } from "./app-shell";
import { ON_PORTAL } from "./portal-env";

// Browser fullscreen for the whole app. One rule: once you are in it, you stay
// in it for the rest of the visit, on every page, until YOU leave (Escape, the
// back gesture, the ⛶ button). Three things get you in:
//   - the ⛶ button in the nav bar and the in-game toolbar (FullscreenButton),
//   - a match starting (App.tsx, gated by the "Full screen during matches"
//     setting), which is how most people first land in it,
//   - the landscape pill on phones (FullscreenNudge).
// A fullscreen request only succeeds inside a user gesture (tap, click, key),
// so callers run it synchronously in their handler. Everything is best effort
// (iPhone Safari has no page fullscreen at all).

export function fullscreenSupported(): boolean {
  if (typeof document === "undefined") return false;
  // The Android app is full screen from the start (apps/android): no button, no nudge.
  if (inAndroidApp()) return false;
  // A game site has its own full screen button and allows no other (CrazyGames).
  if (ON_PORTAL) return false;
  const el = document.documentElement as any;
  if (!(el.requestFullscreen || el.webkitRequestFullscreen)) return false;
  const enabled = (document as any).fullscreenEnabled ?? (document as any).webkitFullscreenEnabled;
  return enabled !== false;
}

export function isFullscreen(): boolean {
  if (typeof document === "undefined") return false;
  return !!(document.fullscreenElement || (document as any).webkitFullscreenElement);
}

export function enterFullscreen(): void {
  if (isFullscreen() || !fullscreenSupported()) return;
  const el = document.documentElement as any;
  try {
    // Older Safari only has the prefixed call, which swallows letter keys in
    // fullscreen unless asked to allow keyboard input (the game is steered by keys).
    const result = el.requestFullscreen
      ? el.requestFullscreen()
      : el.webkitRequestFullscreen((Element as any).ALLOW_KEYBOARD_INPUT);
    Promise.resolve(result).catch(() => {});
  } catch {
    /* ignore */
  }
}

export function exitFullscreen(): void {
  if (!isFullscreen()) return;
  const exit = (document as any).exitFullscreen || (document as any).webkitExitFullscreen;
  if (!exit) return;
  try {
    Promise.resolve(exit.call(document)).catch(() => {});
  } catch {
    /* ignore */
  }
}

export function toggleFullscreen(): void {
  if (isFullscreen()) exitFullscreen();
  else enterFullscreen();
}

// Screen orientation lock, best effort: browsers only grant it in fullscreen
// (Android), and iPhone Safari never does.
export function lockOrientation(kind: "portrait" | "landscape"): void {
  try {
    const so: any = typeof screen !== "undefined" ? screen.orientation : null;
    if (so && typeof so.lock === "function") Promise.resolve(so.lock(kind)).catch(() => {});
  } catch {
    /* ignore */
  }
}
export function unlockOrientation(): void {
  try {
    const so: any = typeof screen !== "undefined" ? screen.orientation : null;
    if (so && typeof so.unlock === "function") so.unlock();
  } catch {
    /* ignore */
  }
}

// Phones play sideways only (RotateScreen covers an upright one). A phone is a
// touch screen whose short side is phone sized, so tablets keep turning freely.
export function isPhone(): boolean {
  try {
    if (typeof matchMedia !== "function" || !matchMedia("(pointer: coarse)").matches) return false;
    return Math.min(screen.width, screen.height) <= 500;
  } catch {
    return false;
  }
}

type Listener = (on: boolean) => void;
const listeners = new Set<Listener>();
export function subscribeFullscreen(fn: Listener): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}
if (typeof document !== "undefined") {
  const onChange = () => {
    const on = isFullscreen();
    // Every way into fullscreen (the button, the pill, a match starting) ends up
    // here, and the lock is only granted once fullscreen has landed: so a phone
    // is held in landscape from this one place. Leaving fullscreen frees it.
    if (isPhone()) {
      if (on) lockOrientation("landscape");
      else unlockOrientation();
    }
    listeners.forEach((fn) => {
      try {
        fn(on);
      } catch {
        /* a misbehaving listener must not break the rest */
      }
    });
  };
  document.addEventListener("fullscreenchange", onChange);
  document.addEventListener("webkitfullscreenchange", onChange);
}

// Live fullscreen state for the buttons.
export function useFullscreen(): boolean {
  const [on, setOn] = useState(isFullscreen);
  useEffect(() => subscribeFullscreen(setOn), []);
  return on;
}
